// src/components/block/liveStreamManagment/useAutoDirector.ts
// v1.0 — Réalisateur auto : branche le moteur (autoDirector.ts) sur la régie.
//
// Ce que le hook lit : les niveaux par source dans `audioLevelStore` (hors React,
// aucun rendu au rythme des VU-mètres). Ce qu'il écrit : rien lui-même — il
// appelle `onSwitch(id)`, qui est le clic sur le deck (`handleDeckSwitch`). La
// bascule suit donc exactement le chemin d'un clic d'opérateur : acquisition,
// file d'attente, garde d'un flux déjà à l'antenne.
//
// Réglages persistés par chaîne (comme les autres réglages de régie) ; l'état
// « activé », lui, ne l'est jamais : une régie qui reprend la main toute seule au
// chargement de la page serait une mauvaise surprise en direct.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { audioLevelStore } from '../../../dataTypes/liveStreamManagment/audioPipeline/audioLevelStore';
import {
  AutoDirectorEngine, AutoDirectorSettings,
  DEFAULT_AUTO_DIRECTOR_SETTINGS, sanitizeAutoDirectorSettings,
} from './autoDirector';

const TICK_MS = 100;

/** 'off' éteint · 'active' décide · 'paused' laisse la main à l'opérateur · 'blocked' ne peut pas décider. */
export type AutoDirectorStatus = 'off' | 'active' | 'paused' | 'blocked';

export interface UseAutoDirectorOptions {
  channelUuid : string | null;
  /** Sources du deck (id + libellé). Les sources éteintes ou absentes du mixer n'y figurent pas. */
  deckSourceIds : string[];
  /** Source actuellement au final. */
  currentId   : string | null;
  /**
   * Vrai quand la régie peut effectivement décider : ce poste tient la main, la
   * régie est sous tension, aucune scène n'est au final.
   */
  canDecide   : boolean;
  onSwitch    : (sourceId: string) => void;
}

export interface UseAutoDirectorReturn {
  enabled       : boolean;
  setEnabled    : (on: boolean) => void;
  settings      : AutoDirectorSettings;
  setSettings   : (next: AutoDirectorSettings) => void;
  status        : AutoDirectorStatus;
}

const storageKey = (uuid: string | null) =>
  uuid ? `mediartc_regie_auto_director:${uuid}` : 'mediartc_regie_auto_director';

function loadSettings(key: string): AutoDirectorSettings {
  try {
    const raw = localStorage.getItem(key);
    return raw ? sanitizeAutoDirectorSettings(JSON.parse(raw)) : DEFAULT_AUTO_DIRECTOR_SETTINGS;
  } catch { return DEFAULT_AUTO_DIRECTOR_SETTINGS; }
}

export function useAutoDirector(opts: UseAutoDirectorOptions): UseAutoDirectorReturn {
  const { channelUuid, deckSourceIds, currentId, canDecide, onSwitch } = opts;

  const key = storageKey(channelUuid);
  const [settings, setSettingsState] = useState<AutoDirectorSettings>(() => loadSettings(key));
  const [enabled,  setEnabledState]  = useState(false);
  const [status,   setStatus]        = useState<AutoDirectorStatus>('off');

  // L'uuid de la chaîne est souvent inconnu au montage : la clé change ensuite, et
  // les réglages de CETTE chaîne doivent être relus, pas ceux de la précédente.
  // Un changement de chaîne éteint aussi le réalisateur.
  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    setSettingsState(loadSettings(key));
    setEnabledState(false);
  }, [key]);

  // Reprise serveur (autre poste) : les réglages viennent d'être écrits, on les relit.
  useEffect(() => {
    const onRestored = (e: Event) => {
      const d = (e as CustomEvent<{ uuid: string; settings: boolean }>).detail;
      if (d?.settings && d.uuid === channelUuid) setSettingsState(loadSettings(key));
    };
    window.addEventListener('regie-deck-restored', onRestored);
    return () => window.removeEventListener('regie-deck-restored', onRestored);
  }, [channelUuid, key]);

  const setSettings = useCallback((next: AutoDirectorSettings) => {
    const clean = sanitizeAutoDirectorSettings(next);
    setSettingsState(clean);
    try { localStorage.setItem(key, JSON.stringify(clean)); } catch {}
  }, [key]);

  const engineRef = useRef<AutoDirectorEngine>(new AutoDirectorEngine());

  // Valeurs lues par la boucle : dans des refs, pour que l'intervalle ne soit pas
  // recréé à chaque changement de source ou de réglage.
  const settingsRef   = useRef(settings);
  const currentIdRef  = useRef(currentId);
  const canDecideRef  = useRef(canDecide);
  const onSwitchRef   = useRef(onSwitch);
  const deckIdsRef    = useRef(deckSourceIds);
  settingsRef.current  = settings;
  currentIdRef.current = currentId;
  canDecideRef.current = canDecide;
  onSwitchRef.current  = onSwitch;
  deckIdsRef.current   = deckSourceIds;

  // Dernier id demandé par le réalisateur : sert à distinguer, dans les changements de
  // `currentId`, ce que nous avons demandé de ce que l'opérateur a fait lui-même.
  const requestedIdRef = useRef<string | null>(null);
  const seenCurrentRef = useRef<string | null>(currentId);

  useEffect(() => {
    if (!enabled) { setStatus('off'); return; }

    engineRef.current.reset();
    seenCurrentRef.current = currentIdRef.current;
    requestedIdRef.current = null;

    const id = window.setInterval(() => {
      const now = performance.now();
      const engine = engineRef.current;
      const cur = currentIdRef.current;
      const s = settingsRef.current;

      // Un changement de final que nous n'avons pas demandé est un choix de
      // l'opérateur : on lui laisse la main le temps réglé.
      if (cur !== seenCurrentRef.current) {
        const manual = cur !== requestedIdRef.current;
        seenCurrentRef.current = cur;
        if (manual && s.manualPauseMs > 0) engine.pauseUntil(now + s.manualPauseMs);
      }

      const deckIds   = deckIdsRef.current;
      const candidates = s.candidateIds ? deckIds.filter(x => s.candidateIds!.includes(x)) : deckIds;

      const levels = new Map<string, number>();
      for (const cid of candidates) levels.set(cid, audioLevelStore.get(cid).avg);

      const pick = engine.tick(s, candidates, { now, levels, currentId: cur });

      const next: AutoDirectorStatus =
        !canDecideRef.current ? 'blocked' : engine.isPaused(now) ? 'paused' : 'active';
      setStatus(prev => (prev === next ? prev : next));

      if (pick && canDecideRef.current) {
        requestedIdRef.current = pick;
        onSwitchRef.current(pick);
      }
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, [enabled]);

  const setEnabled = useCallback((on: boolean) => setEnabledState(on), []);

  return useMemo(
    () => ({ enabled, setEnabled, settings, setSettings, status }),
    [enabled, setEnabled, settings, setSettings, status],
  );
}
